import { useEffect, useState } from 'react'
import { paidInPerson } from '../lib/storePayment'
import { useNavigate } from 'react-router-dom'
import { ArrowDownCircle, ArrowUpCircle, RefreshCw, Receipt, ChevronDown, ExternalLink, CreditCard, Wallet, Briefcase, Home, Pencil, Download, Bike, ChevronRight, Banknote } from 'lucide-react'
import Header from '../components/Header'
import { useStore, type Transaction } from '../store/useStore'
import { useMyOrders } from '../hooks/useMyOrders'
import { useI18n } from '../i18n/useI18n'
import { NETWORK } from '../lib/network'
import { isWeb3Mode } from '../lib/authMode'
import { AUTO_TOPUP_CARD_FEE_FIXED, AUTO_TOPUP_CARD_FEE_RATE } from '../hooks/useAutoTopUp'
import { genreOf, usesReceiptWord } from '../lib/genre'
import VariationLine from '../components/VariationLine'
import RefundVoucherCard from '../components/RefundVoucherCard'

function formatDate(date: Date, locale: string): string {
  const d = new Date(date)
  if (locale === 'ja') {
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
  }
  if (locale === 'zh') {
    return `${d.getMonth() + 1}月${d.getDate()}日 ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
}

function formatFullTimestamp(date: Date): string {
  const d = new Date(date)
  return d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC'
}

// Generate a deterministic mock tx hash from tx id
function mockTxHash(txId: string): string {
  let hash = 0
  for (let i = 0; i < txId.length; i++) {
    const char = txId.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash |= 0
  }
  const hex = Math.abs(hash).toString(16).padStart(8, '0')
  return `0x${hex}${'a1b2c3d4e5f6'.repeat(5)}`.slice(0, 66)
}

// カードの手数料 (固定 + 料率) は useAutoTopUp と同じ値を使う (#1190 で 4.4% → 2%。
// ここに別の数字を持っていて履歴だけ 4.4% のまま残っていた)
const FIXED_FEE = AUTO_TOPUP_CARD_FEE_FIXED
const FEE_RATE = AUTO_TOPUP_CARD_FEE_RATE

// #1764 履歴に出す「どの方法でチャージしたか」
const CHARGE_METHOD_KEY: Record<NonNullable<Transaction['chargeMethod']>, string> = {
  card: 'history.chargeMethodCard',
  bank_transfer: 'history.chargeMethodBankTransfer',
  revolut: 'history.chargeMethodRevolut',
  alipay: 'history.chargeMethodAlipay',
  wechat_pay: 'history.chargeMethodWeChatPay',
  local_payment: 'history.chargeMethodLocalPayment',
}

const EXPENSE_CATEGORY_KEYS = ['meals', 'supplies', 'entertainment', 'meeting', 'travel', 'other'] as const
type ExpenseCategoryKey = typeof EXPENSE_CATEGORY_KEYS[number]

/**
 * #1744 カード返金が Stripe 側で失敗し、店舗の代替返金も無い (システムの自動記録 'none') 場合は
 * お客様に戻っていないので「返金なし」として金額を出す。'cash' 等の代替返金は戻っている。
 */
function refundNotReceived(refund: unknown): boolean {
  const r = (refund || {}) as { method?: string; stripeRefundStatus?: string; altRefund?: { method?: string } | null }
  if (r.method === 'cash') return false
  const failed = r.stripeRefundStatus === 'failed' || r.stripeRefundStatus === 'canceled'
  return failed && (!r.altRefund || r.altRefund.method === 'none')
}

function effectiveRefundAmount(refund: unknown): number {
  if (refundNotReceived(refund)) return 0
  return ((refund || {}) as { amount?: number }).amount || 0
}

export default function TransactionHistory() {
  const navigate = useNavigate()
  const { transactions, setExpenseTag, refreshTransactions } = useStore()
  const { t, locale } = useI18n()

  // #942 現金会計: pull fresh transactions on mount. Cash receipts are
  // recorded server-side when the store confirms payment, so they won't be
  // in local state until we re-fetch (Stripe updates state optimistically).
  useEffect(() => {
    refreshTransactions()
  }, [refreshTransactions])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null)
  const [editTag, setEditTag] = useState<'business' | 'personal' | null>(null)
  const [editCategory, setEditCategory] = useState<ExpenseCategoryKey>('meals')
  const [editMemo, setEditMemo] = useState('')
  const [showExportMenu, setShowExportMenu] = useState(false)

  // Show newest first (sort by timestamp descending)
  const sortedTransactions = [...transactions].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )

  // #338 bridge back to /my-orders for delivery orders that were delivered
  // within the last 30 minutes. After the MyOrders card disappears, this
  // is the customer's only remaining path to re-open the chat / file
  // "届いていない" — critical for genuinely missing orders. Matches the
  // GRACE_ACTIVE_MS in MyOrders.tsx. Orders already reporting missing are
  // still on /my-orders so they're excluded here to avoid duplication.
  const { orders: myOrders } = useMyOrders()
  const RECENT_DELIVERY_WINDOW_MS = 30 * 60 * 1000
  // Date.now() during render trips react-hooks/purity. Refresh `now` every
  // minute via effect so the banner ages out even if the user keeps the
  // page open without any other state change.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])
  const recentDeliveries = myOrders.filter((o) => {
    if (o.orderType !== 'delivery') return false
    if (o.status !== 'delivered' && o.status !== 'cancelled') return false
    if (o.missingReported) return false
    const ts = o.updatedAt || o.createdAt
    if (!ts) return false
    const age = now - new Date(ts).getTime()
    return age >= 0 && age < RECENT_DELIVERY_WINDOW_MS
  })

  function openExpenseEdit(tx: { id: string; expenseTag?: string; expenseCategory?: string; expenseMemo?: string }) {
    setEditingExpenseId(tx.id)
    setEditTag((tx.expenseTag as 'business' | 'personal') ?? null)
    setEditCategory((tx.expenseCategory as ExpenseCategoryKey) ?? 'meals')
    setEditMemo(tx.expenseMemo ?? '')
  }

  function saveExpenseEdit(txId: string) {
    setExpenseTag(txId, editTag, editTag === 'business' ? editCategory : undefined, editTag === 'business' ? editMemo : undefined)
    setEditingExpenseId(null)
  }

  function exportCsv(filter: 'all' | 'business' | 'personal') {
    setShowExportMenu(false)
    const paymentTxs = sortedTransactions.filter((tx) => tx.type === 'payment')
    const filtered =
      filter === 'all'
        ? paymentTxs
        : paymentTxs.filter((tx) => tx.expenseTag === filter)

    if (filtered.length === 0) {
      alert(t('expense.noTx'))
      return
    }
    const header = ['日付', '支払先', '金額(JPY)', '勘定科目', '摘要', '経費区分', '決済方法']
    const rows = filtered.map((tx) => [
      new Date(tx.timestamp).toISOString().slice(0, 10),
      tx.storeName ?? '',
      String(tx.amount),
      tx.expenseCategory ? t(`expense.cat.${tx.expenseCategory}`) : '',
      tx.expenseMemo ?? '',
      tx.expenseTag === 'business' ? t('expense.business') : tx.expenseTag === 'personal' ? t('expense.personal') : '',
      // #1190 Phase 4: CSV も画面表示と同じ「YOKOSO 通貨」表記に統一 (methodPoints key 経由)
      tx.paymentMethod === 'stripe' ? 'クレジットカード' : tx.paymentMethod === 'cash' ? '現金'
        : tx.paymentMethod === 'store_terminal' ? t('payment.methodStoreTerminal') : t('payment.methodPoints'),
    ])
    const csv = [header, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const suffix = filter === 'business' ? '-business' : filter === 'personal' ? '-personal' : '-all'
    a.download = `yokoso-expense${suffix}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      <Header
        title={t('history.title')}
        onBack={() => navigate(-1)}
        rightAction={
          <button
            onClick={() => setShowExportMenu(true)}
            className="flex items-center gap-1 rounded-lg px-2 py-1 font-body text-xs text-text-secondary hover:text-navy tap-effect"
          >
            <Download size={14} />
            {t('expense.exportCsv')}
          </button>
        }
      />

      {sortedTransactions.length === 0 ? (
        /* Empty State */
        <div className="flex flex-1 flex-col items-center justify-center px-8">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
            <Receipt size={36} className="text-text-tertiary" />
          </div>
          <p className="mt-4 font-display text-lg font-semibold text-text-primary">
            {t('history.noTransactions')}
          </p>
          <p className="mt-2 text-center font-body text-sm text-text-secondary">
            {t('history.noTransactionsDesc')}
          </p>
        </div>
      ) : (
        /* Transaction List */
        <div className="flex-1 px-4 pt-4 pb-6">
          {/* #338 recent-delivery bridge: last 30 minutes of delivered
            * orders still have "届いていない" + chat available via
            * /my-orders; surface the link here so the customer doesn't
            * get stuck once the card aged out of Home. */}
          {recentDeliveries.length > 0 && (
            <button
              onClick={() => navigate('/my-orders')}
              className="w-full mb-3 tap-effect rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-center gap-3 text-left"
            >
              <div className="h-9 w-9 shrink-0 rounded-full bg-amber-100 flex items-center justify-center">
                <Bike size={16} className="text-amber-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-sm text-amber-900">
                  {t('history.recentDeliveryBannerTitle', { count: recentDeliveries.length })}
                </p>
                <p className="font-body text-[11px] text-amber-800 mt-0.5 leading-relaxed">
                  {t('history.recentDeliveryBannerDesc')}
                </p>
              </div>
              <ChevronRight size={16} className="shrink-0 text-amber-700" />
            </button>
          )}
          <div className="space-y-3">
            {sortedTransactions.map((tx) => {
              const isCharge = tx.type === 'charge'
              const isAdjustment = tx.type === 'adjustment'
              const isExpanded = expandedId === tx.id
              // #942 現金は on-chain ではないので blockchain (Tx Hash / Network /
              // explorer) 行を出さない。stripe も同様に出さない（= JPYC 限定）。
              // legacy で paymentMethod 未設定の JPYC tx は従来どおり表示する。
              // #1764 既定の Firebase mode は残高を Firestore で持つだけで、チェーン上に
              // 何も発行していない (txHash は receipt 用の乱数)。backend が onChain を
              // 付けた取引 (カード (デモ) の TestJPYC mint) と Web3 mode だけ出す。
              const isJpyc = (isWeb3Mode() || tx.onChain === true) && tx.paymentMethod !== 'stripe' && !paidInPerson(tx.paymentMethod)
              const realTxHash = tx.txHash
              const txHash = realTxHash || mockTxHash(tx.id)
              const shortHash = `${txHash.slice(0, 10)}...${txHash.slice(-6)}`
              const explorerLink = tx.explorerLink || (realTxHash ? `${NETWORK.blockExplorerUrl}/tx/${realTxHash}` : null)

              // Fee breakdown (固定 + 料率) はカードのチャージだけ。銀行振込 / Revolut は
              // 手数料 0、Alipay / WeChat は料率のみなので、この内訳を当てはめると負の値になる
              // chargeMethod を持たない旧チャージは、固定 350 円が乗っていればカード
              // (固定手数料があるのはカードだけ)。それ以外は経路不明として出さない
              const chargeMethod = tx.chargeMethod
                ?? (tx.onChain || (tx.fee ?? 0) >= FIXED_FEE ? 'card' : undefined)
              const isCardCharge = isCharge && chargeMethod === 'card'
              const processingFee = tx.fee ? tx.fee - FIXED_FEE : Math.round(tx.amount * FEE_RATE)
              const processingFeePct = tx.fee && tx.amount > 0
                ? Math.round((processingFee / tx.amount) * 1000) / 10
                : FEE_RATE * 100
              const fixedFee = FIXED_FEE

              return (
                <div
                  key={tx.id}
                  className="animate-fade-in rounded-xl bg-white shadow-card overflow-hidden"
                >
                  {/* Main row */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : tx.id)}
                    className="flex w-full items-center gap-3 p-4 tap-effect"
                  >
                    {/* Icon */}
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                        isAdjustment ? 'bg-blue-50' : isCharge ? 'bg-matcha/10' : 'bg-yokoso-red/10'
                      }`}
                    >
                      {isAdjustment ? (
                        <RefreshCw size={22} className="text-blue-500" />
                      ) : isCharge ? (
                        <ArrowDownCircle size={22} className="text-matcha" />
                      ) : (
                        <ArrowUpCircle size={22} className="text-yokoso-red" />
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-display text-sm font-semibold text-text-primary">
                          {isAdjustment ? t('history.adjustment') : isCharge ? t('history.charge') : tx.storeName ?? t('history.payment')}
                        </p>
                        <div className="text-right">
                          {tx.refund ? (
                            <>
                              <p className="font-display text-sm font-bold text-text-primary">
                                &yen;{(tx.amount - effectiveRefundAmount(tx.refund)).toLocaleString()}
                              </p>
                              <p className="font-body text-[10px] text-text-tertiary line-through">
                                &yen;{tx.amount.toLocaleString()}
                              </p>
                            </>
                          ) : (
                            <p
                              className={`font-display text-sm font-bold ${
                                isAdjustment ? 'text-blue-500' : isCharge ? 'text-matcha' : 'text-text-primary'
                              }`}
                            >
                              {isAdjustment ? (tx.amount > 0 ? '+' : '') : isCharge ? '+' : ''}&yen;{tx.amount.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="font-body text-xs text-text-tertiary">
                          {formatDate(tx.timestamp, locale)}
                          {/* #1764 どの方法でチャージしたか (銀行振込 / Revolut / カード / Alipay・WeChat) */}
                          {isCharge && chargeMethod && (
                            <> · {t(CHARGE_METHOD_KEY[chargeMethod])}</>
                          )}
                        </span>
                        <div className="flex items-center gap-2">
                          {/* Fee belongs to the customer only for charges
                            * (wallet top-up): the fee is added to what they
                            * pay. For payment txs the fee lives on the
                            * merchant's Stripe Connect application_fee and
                            * is invisible to the customer — surfacing it
                            * here made customers think they were charged
                            * extra on their delivery order. */}
                          {!tx.refund && tx.type === 'charge' && tx.fee != null && tx.fee > 0 && (
                            <span className="font-body text-xs text-text-tertiary">
                              {t('history.fee')}: &yen;{tx.fee.toLocaleString()}
                            </span>
                          )}
                          {/* 決済方法バッジ（paymentのみ） */}
                          {tx.type === 'payment' && tx.paymentMethod === 'stripe' && (
                            <span className="flex items-center gap-0.5 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">
                              <CreditCard size={9} />
                              Card
                            </span>
                          )}
                          {/* #942 現金: blockchain (JPYC) ではないので別バッジ */}
                          {tx.type === 'payment' && tx.paymentMethod === 'cash' && (
                            <span className="flex items-center gap-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                              <Banknote size={9} />
                              {t('payment.methodCash')}
                            </span>
                          )}
                          {/* #2162 お店の端末 (STORES 決済・Airペイなど)。YOKOSO 通貨ではない */}
                          {tx.type === 'payment' && tx.paymentMethod === 'store_terminal' && (
                            <span className="flex items-center gap-0.5 rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-medium text-teal-700">
                              <CreditCard size={9} />
                              {t('payment.methodStoreTerminal')}
                            </span>
                          )}
                          {tx.type === 'payment' && tx.paymentMethod !== 'stripe' && !paidInPerson(tx.paymentMethod) && (
                            // #1190 Phase 4: chip 表記も他表示 (methodPoints = "YOKOSO 通貨") と統一
                            <span className="flex items-center gap-0.5 rounded-full bg-matcha/10 px-2 py-0.5 text-[10px] font-medium text-matcha">
                              <Wallet size={9} />
                              {t('payment.methodPoints')}
                            </span>
                          )}
                          {tx.refund ? (() => {
                            // #533: Stripe refund の status を見て表示分岐:
                            // succeeded or altRefund 記録済 → 「カード返金済」(緑)
                            // failed/canceled (altRefund 未記録) → 「失敗」(赤、店舗対応待ち)
                            // pending or undefined (legacy) → 「カード返金中」(現行)
                            const refundData = tx.refund as {
                              method?: string;
                              stripeRefundStatus?: string;
                              altRefund?: { method?: string };
                            }
                            const isCash = refundData.method === 'cash'
                            // #2162 お店の決済端末で返金 (記録だけ)。現金と同じく完了で、表記は別
                            const isStoreTerminal = refundData.method === 'store_terminal'
                            const isInPerson = isCash || isStoreTerminal
                            const stripeStatus = refundData.stripeRefundStatus
                            // #1744 システムの自動記録 (method='none') は「返金なし」。完了に見せない
                            const hasAltRefund = !!refundData.altRefund && refundData.altRefund.method !== 'none'
                            // 店舗が代替返金マーク済 → 顧客 UI 上は完了扱い
                            const isStripeDone = !isInPerson && (stripeStatus === 'succeeded' || hasAltRefund)
                            const isStripeFailed = !isInPerson && !hasAltRefund && (stripeStatus === 'failed' || stripeStatus === 'canceled')
                            const badgeClass = isInPerson
                              ? 'bg-orange-100 text-orange-600'
                              : isStripeDone
                                ? 'bg-matcha/15 text-matcha'
                                : isStripeFailed
                                  ? 'bg-red-200 text-red-700'
                                  : 'bg-red-100 text-red-600'
                            const label = isStoreTerminal
                              ? t('history.storeTerminalRefunded')
                              : isCash
                              ? t('history.cashRefunded')
                              : isStripeDone
                                ? t('history.stripeRefunded')
                                : isStripeFailed
                                  ? t('history.stripeRefundFailed')
                                  : t('history.stripeRefunding')
                            return (
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badgeClass}`}>
                                {label}
                              </span>
                            )
                          })() : (
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                tx.status === 'completed'
                                  ? 'bg-matcha/10 text-matcha'
                                  : tx.status === 'pending'
                                    ? 'bg-gold/10 text-gold'
                                    : 'bg-red-100 text-red-600'
                              }`}
                            >
                              {t(`history.${tx.status}`)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expand chevron */}
                    <ChevronDown
                      size={16}
                      className={`shrink-0 text-text-disabled transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 space-y-2.5">
                      {/* #968 レシート番号（取引ID）— 店舗への問い合わせ/経費精算の参照番号 */}
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-body text-xs text-text-tertiary shrink-0">
                          {t('history.receiptNo')}
                        </span>
                        <span className="font-mono text-[11px] font-medium text-text-secondary break-all text-right">
                          {tx.id}
                        </span>
                      </div>

                      {/* #1207 品目明細 (卓会計/物販由来の tx のみ — QR 手入力決済は
                          明細データ自体が存在しないため金額のみ) */}
                      {tx.items && tx.items.length > 0 && (
                        <div className="space-y-1 rounded-lg bg-white border border-gray-100 px-3 py-2">
                          {tx.items.map((i, idx) => (
                            <div key={idx} className="flex justify-between gap-2 font-body text-xs">
                              <span className="text-text-secondary">
                                {locale === 'ja' ? (i.name || i.nameEn) : (i.nameEn || i.name)}
                                {(i.quantity || 1) > 1 && (
                                  <span className="ml-1 text-text-tertiary">×{i.quantity}</span>
                                )}
                                {/* #2031 レシートと同じく、選んだ内容を品名の下に出す */}
                                <VariationLine item={i} />
                              </span>
                              <span className="shrink-0 text-text-primary">
                                ¥{((i.price || 0) * (i.quantity || 1)).toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* #1168 Web領収書 / #1207 Webレシート (明細+EMV) へ */}
                      {tx.type === 'payment' && tx.status === 'completed' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/receipt/${tx.id}?view=receipt`)}
                            className="tap-effect flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-navy/20 py-2 font-body text-xs font-medium text-navy hover:bg-navy/5 transition-colors"
                          >
                            <Receipt size={14} />
                            {t(usesReceiptWord(tx.storeGenre || genreOf({ category: tx.storeCategory })) ? 'history.viewReceipt' : 'history.viewReceiptStatement')}
                          </button>
                          <button
                            onClick={() => navigate(`/receipt/${tx.id}`)}
                            className="tap-effect flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-navy/20 py-2 font-body text-xs font-medium text-navy hover:bg-navy/5 transition-colors"
                          >
                            <Receipt size={14} />
                            {t('receipt.issueButton')}
                          </button>
                        </div>
                      )}

                      {/* 決済方法 */}
                      {tx.type === 'payment' && tx.paymentMethod && (
                        <div className="flex items-center justify-between">
                          <span className="font-body text-xs text-text-tertiary">
                            {t('history.paymentMethod')}
                          </span>
                          <span className="font-body text-xs font-medium text-text-primary">
                            {tx.paymentMethod === 'stripe'
                              ? t('payment.methodStripe')
                              : tx.paymentMethod === 'cash'
                                ? t('payment.methodCash')
                                : tx.paymentMethod === 'store_terminal'
                                  ? t('payment.methodStoreTerminal')
                                  : t('payment.methodPoints')}
                          </span>
                        </div>
                      )}

                      {/* Refund info */}
                      {tx.refund && (() => {
                        const ref = tx.refund as {
                          amount: number;
                          reason?: string;
                          method?: string;
                          fee?: number;
                          stripeRefundStatus?: string;
                          altRefund?: { method?: string; completedAt?: string; voucherId?: string };
                        }
                        const refAmt = effectiveRefundAmount(ref)
                        const actual = tx.amount - refAmt
                        // #2190 二重のお支払いの自動の返金 (お客様都合でもお店都合でもない)
                        const isDuplicate = ref.reason === 'duplicate'
                        const isCustomerFault = ref.reason !== 'store' && !isDuplicate
                        // #2162 お店の決済端末の返金も店頭で返したもの (現金と同じ扱い、表記は別)
                        const isStoreTerminal = ref.method === 'store_terminal'
                        const isCash = ref.method === 'cash' || isStoreTerminal
                        const refundFeeFromDb = ref.fee || 0
                        const stripeStatus = ref.stripeRefundStatus
                        const hasAltRefund = !!ref.altRefund && ref.altRefund.method !== 'none'
                        const isStripeDone = !isCash && (stripeStatus === 'succeeded' || hasAltRefund)
                        const isStripeFailed = !isCash && !hasAltRefund && (stripeStatus === 'failed' || stripeStatus === 'canceled')
                        return (
                          <div className={`rounded-lg p-2.5 space-y-1.5 ${isCash ? 'bg-orange-50' : 'bg-red-50'}`}>
                            <div className="flex items-center justify-between font-body text-xs">
                              <span className="text-text-secondary">{t('history.refundMethod')}</span>
                              <span className={`font-medium ${isCash ? 'text-orange-600' : 'text-red-600'}`}>
                                {isStoreTerminal ? t('payment.methodStoreTerminal') : isCash ? t('history.methodCash') : t('history.methodStripe')}
                              </span>
                            </div>
                            {!isCash && (
                              <div className="flex items-center justify-between font-body text-xs">
                                <span className="text-text-secondary">{t('history.refundStatus')}</span>
                                <span
                                  className={`font-medium ${
                                    isStripeDone
                                      ? 'text-matcha'
                                      : isStripeFailed
                                        ? 'text-red-700'
                                        : 'text-gold'
                                  }`}
                                >
                                  {isStripeDone
                                    ? t('history.refundCompleted')
                                    : isStripeFailed
                                      ? t('history.refundFailedStatus')
                                      : t('history.refundPending')}
                                </span>
                              </div>
                            )}
                            {isStripeFailed && (
                              // #2308 カードに返せなかった返金は、お店で引換券を見せて別の方法で返してもらう
                              // (引換券は毎朝のジョブが用意する。まだ無ければカードがそう出す)。
                              // #2476 申し立て (チャージバック) が来ていればカード会社から戻るので、引換券の案内はしない
                              <div className="rounded-md bg-red-100 border border-red-200 p-2 font-body text-xs text-red-700" data-testid="sale-refund-failed">
                                {tx.refundDisputed ? (
                                  t('refundOnline.disputed')
                                ) : (
                                  <>
                                    {t('history.refundFailedVoucher')}
                                    <RefundVoucherCard transactionId={tx.id} />
                                  </>
                                )}
                              </div>
                            )}
                            {!isCash && ref.altRefund?.voucherId && (
                              <p className="font-body text-xs text-matcha" data-testid="sale-refund-alternate">
                                {t('history.refundAlternateDone')}
                              </p>
                            )}
                            <div className="flex items-center justify-between font-body text-xs">
                              <span className="text-text-secondary">{t('history.refundReason')}</span>
                              <span className="font-medium text-text-primary">
                                {isDuplicate ? t('history.reasonDuplicate')
                                  : isCustomerFault ? t('history.reasonCustomer') : t('history.reasonStore')}
                              </span>
                            </div>
                            <div className="flex items-center justify-between font-body text-xs">
                              <span className="text-text-secondary">{t('history.originalPayment')}</span>
                              <span className="text-text-primary">&yen;{tx.amount.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center justify-between font-body text-xs">
                              <span className="text-matcha">{t('history.refundBack')}</span>
                              {/* #1744 返金が成立していなければ「−¥0」ではなく ¥0 */}
                              <span className="font-medium text-matcha">{refAmt > 0 ? <>&minus;&yen;{refAmt.toLocaleString()}</> : <>&yen;0</>}</span>
                            </div>
                            {refundFeeFromDb > 0 && (
                              <div className="flex items-center justify-between font-body text-xs">
                                <span className="text-text-tertiary">{t('history.refundFee')}</span>
                                <span className="text-text-tertiary">&yen;{refundFeeFromDb.toLocaleString()}</span>
                              </div>
                            )}
                            <div className={`flex items-center justify-between font-body text-xs border-t pt-1.5 ${isCash ? 'border-orange-100' : 'border-red-100'}`}>
                              <span className="font-medium text-text-primary">{t('history.actualPayment')}</span>
                              <span className="font-display text-sm font-bold text-text-primary">&yen;{actual.toLocaleString()}</span>
                            </div>
                          </div>
                        )
                      })()}

                      {/* Expense tag (payment only) */}
                      {tx.type === 'payment' && (
                        <div>
                          {editingExpenseId === tx.id ? (
                            /* Inline edit panel */
                            <div className="rounded-lg bg-white border border-gray-200 p-3 space-y-2.5">
                              <p className="font-display text-xs font-semibold text-text-primary">
                                {t('expense.label')}
                              </p>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditTag('business')}
                                  className={`flex flex-1 items-center justify-center gap-1 h-8 rounded-lg border-2 text-xs font-medium transition-colors ${
                                    editTag === 'business' ? 'border-navy bg-navy text-white' : 'border-gray-200 text-text-secondary'
                                  }`}
                                >
                                  <Briefcase size={11} />{t('expense.business')}
                                </button>
                                <button
                                  onClick={() => setEditTag('personal')}
                                  className={`flex flex-1 items-center justify-center gap-1 h-8 rounded-lg border-2 text-xs font-medium transition-colors ${
                                    editTag === 'personal' ? 'border-matcha bg-matcha text-white' : 'border-gray-200 text-text-secondary'
                                  }`}
                                >
                                  <Home size={11} />{t('expense.personal')}
                                </button>
                                <button
                                  onClick={() => { setExpenseTag(tx.id, null); setEditingExpenseId(null) }}
                                  className="flex items-center justify-center h-8 px-2 rounded-lg border-2 border-gray-200 text-xs text-text-disabled"
                                >
                                  {t('expense.untagged')}
                                </button>
                              </div>
                              {editTag === 'business' && (
                                <>
                                  <div className="flex flex-wrap gap-1">
                                    {EXPENSE_CATEGORY_KEYS.map((key) => (
                                      <button
                                        key={key}
                                        onClick={() => setEditCategory(key)}
                                        className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium border transition-colors ${
                                          editCategory === key ? 'border-navy bg-navy text-white' : 'border-gray-200 text-text-secondary'
                                        }`}
                                      >
                                        {t(`expense.cat.${key}`)}
                                      </button>
                                    ))}
                                  </div>
                                  <input
                                    type="text"
                                    placeholder={t('expense.memoPlaceholder')}
                                    value={editMemo}
                                    onChange={(e) => setEditMemo(e.target.value)}
                                    className="w-full h-8 rounded-lg border border-gray-200 px-2.5 font-body text-xs text-text-primary focus:border-navy focus:outline-none"
                                  />
                                </>
                              )}
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingExpenseId(null)}
                                  className="flex-1 h-8 rounded-lg border border-gray-200 font-body text-xs text-text-secondary"
                                >
                                  {t('expense.cancel')}
                                </button>
                                <button
                                  onClick={() => saveExpenseEdit(tx.id)}
                                  className="flex-1 h-8 rounded-lg bg-navy font-body text-xs font-medium text-white"
                                >
                                  {t('expense.save')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Display row */
                            <div className="flex items-center justify-between">
                              <span className="font-body text-xs text-text-tertiary">{t('expense.label')}</span>
                              <button
                                onClick={() => openExpenseEdit(tx)}
                                className="flex items-center gap-1"
                              >
                                {tx.expenseTag ? (
                                  <span className={`flex items-center gap-0.5 font-body text-xs font-medium ${tx.expenseTag === 'business' ? 'text-navy' : 'text-text-secondary'}`}>
                                    {tx.expenseTag === 'business' ? <Briefcase size={10} /> : <Home size={10} />}
                                    {t(`expense.${tx.expenseTag}`)}
                                    {tx.expenseCategory && <span className="text-text-tertiary ml-1">· {t(`expense.cat.${tx.expenseCategory}`)}</span>}
                                  </span>
                                ) : (
                                  <span className="font-body text-xs text-navy">{t('expense.addTag')}</span>
                                )}
                                <Pencil size={10} className="text-text-disabled" />
                              </button>
                            </div>
                          )}
                          {/* Memo display (if business and not editing) */}
                          {tx.expenseTag === 'business' && tx.expenseMemo && editingExpenseId !== tx.id && (
                            <div className="flex items-start justify-between mt-1">
                              <span className="font-body text-xs text-text-tertiary">{t('expense.memoLabel')}</span>
                              <span className="font-body text-xs text-text-primary text-right max-w-[60%]">{tx.expenseMemo}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Tx Hash (JPYC only) */}
                      {isJpyc && (
                        <div className="flex items-center justify-between">
                          <span className="font-body text-xs text-text-tertiary">
                            {t('history.txHash')}
                          </span>
                          <span className="font-mono text-xs text-navy">
                            {shortHash}
                          </span>
                        </div>
                      )}

                      {/* Full timestamp */}
                      <div className="flex items-center justify-between">
                        <span className="font-body text-xs text-text-tertiary">
                          {t('history.timestamp')}
                        </span>
                        <span className="font-mono text-xs text-text-primary">
                          {formatFullTimestamp(tx.timestamp)}
                        </span>
                      </div>

                      {/* Network (JPYC only) */}
                      {isJpyc && (
                        <div className="flex items-center justify-between">
                          <span className="font-body text-xs text-text-tertiary">
                            {t('history.network')}
                          </span>
                          <span className="font-body text-xs font-medium text-text-primary">
                            {NETWORK.name} (JPYC)
                          </span>
                        </div>
                      )}

                      {/* Fee breakdown (charge only) */}
                      {/* `tx.fee && ...` だと fee=0 のとき "0" が描画される (#1764) */}
                      {isCardCharge && !!tx.fee && tx.fee > 0 && (
                        <>
                          <div className="border-t border-gray-200 pt-2">
                            <span className="font-display text-xs font-semibold text-text-primary">
                              {t('history.feeBreakdown')}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-body text-xs text-text-tertiary">
                              {t('history.fixedFee')}
                            </span>
                            <span className="font-mono text-xs text-text-primary">
                              &yen;{fixedFee.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-body text-xs text-text-tertiary">
                              {t('history.processingFee')} ({processingFeePct}%)
                            </span>
                            <span className="font-mono text-xs text-text-primary">
                              &yen;{processingFee.toLocaleString()}
                            </span>
                          </div>
                        </>
                      )}

                      {/* Block explorer link (JPYC only) */}
                      {isJpyc && <div className="pt-1">
                        {explorerLink ? (
                          <a
                            href={explorerLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-navy"
                          >
                            <ExternalLink size={12} />
                            <span className="font-body text-xs underline underline-offset-2">
                              {t('history.blockExplorer')}
                            </span>
                          </a>
                        ) : (
                          <button className="flex items-center gap-1 text-text-disabled cursor-default">
                            <ExternalLink size={12} />
                            <span className="font-body text-xs">
                              {t('history.blockExplorer')}
                            </span>
                          </button>
                        )}
                      </div>}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Transaction count footer */}
          <p className="mt-4 text-center font-mono text-[10px] text-text-disabled">
            {sortedTransactions.length} transaction(s)
          </p>
        </div>
      )}

      {/* CSV Export bottom sheet */}
      {showExportMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setShowExportMenu(false)}
          />
          {/* Sheet */}
          <div className="fixed inset-x-0 bottom-0 z-50 animate-slide-up rounded-t-2xl bg-white px-4 pb-8 pt-4 safe-bottom shadow-xl">
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-gray-200" />
            <p className="mb-4 font-display text-base font-semibold text-text-primary">
              {t('expense.exportTitle')}
            </p>
            <div className="space-y-2">
              {([
                { filter: 'all', label: t('expense.exportAll'), icon: <Download size={16} />, color: 'text-text-primary' },
                { filter: 'business', label: t('expense.exportBusiness'), icon: <Briefcase size={16} />, color: 'text-navy' },
                { filter: 'personal', label: t('expense.exportPersonal'), icon: <Home size={16} />, color: 'text-matcha' },
              ] as const).map(({ filter, label, icon, color }) => (
                <button
                  key={filter}
                  onClick={() => exportCsv(filter)}
                  className={`tap-effect flex w-full items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 ${color}`}
                >
                  {icon}
                  <span className="font-body text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowExportMenu(false)}
              className="tap-effect mt-3 w-full rounded-xl py-3 font-body text-sm text-text-secondary"
            >
              {t('expense.cancel')}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
